import { createReducer } from "@reduxjs/toolkit";
import { UserActionCreator } from "./userActions.ts";
import {
  handleResetPasswordResponse,
  handleResponse,
  handleUserResponse,
} from "./utils";

const initialState = {
  pending: false,
  error: false,
  errorType: null, // 'field' | 'general'
  errorField: null, // например: 'phone'
  fieldErrors: null, // { phone: 'Такое значение уже существует' }
  errorMessage: null, // общее сообщение (например, для тоста)
  rememberMe: false,

  userData: {
    data: {
      user: null,
      token: null,
    },
  },

  contacts: {
    pending: false,
    error: false,
    data: null,
  },

  updateProfile: {
    pending: false,
    error: false,
    errorMessage: null,
    success: false,
    fieldErrors: {},
  },

  verifyEmail: {
    pending: false,
    error: false,
    errorKind: null as string | null,
    success: false,
  },

  resetPassword: {
    pending: false,
    error: false,
    errorMessage: null,
    success: false,
  },

  notification: {
    isOpen: false,
    value: null,
  },

  payment: {
    pending: false,
    error: false,
    errorMessage: null,
    success: false,
    confirmationUrl: null,
  },

  subscriptionPlans: {
    pending: false,
    error: false,
    errorMessage: null,
    success: false,
    data: null,
  },
  createClient: {
    pending: false,
    error: false,
    errorMessage: null,
    success: false,
    fieldErrors: {},
    data: null,
  },
  clientsBase: {
    pending: false,
    loadingMore: false,
    error: false,
    errorMessage: null,
    data: [],
    meta: {
      perPage: 20,
      nextCursor: null as string | null,
      hasMore: false,
      periodFrom: null as string | null,
      periodTo: null as string | null,
    },
  },
  clientDeals: {
    pending: false,
    error: false,
    errorMessage: null,
    data: {},
    currentLoadingId: null,
  },
  createDeal: {
    pending: false,
    error: false,
    errorMessage: null,
  },
  updateDeal: {
    pending: false,
    error: false,
    errorMessage: null,
  },
  addDealComment: {
    pending: false,
    error: false,
    errorMessage: null,
  },
  attachDealAttachment: {
    pending: false,
    error: false,
    errorMessage: null,
  },
  dealStatuses: {
    pending: false,
    error: false,
    errorMessage: null,
    data: [],   // [{ id, code, name, color, count }, ...]
    total: null as number | null,
  },
};

export const userReducer = createReducer(initialState, (builder) => {
  builder
    // ─────────────────────────────── SIGN IN
    .addCase(UserActionCreator.userSignIn.pending, (state) => {
      state.pending = true;
      state.error = false;
      state.errorMessage = null;
      state.errorType = null;
      state.fieldErrors = null;
    })
    .addCase(UserActionCreator.userSignIn.fulfilled, (state, action) => {
      state.pending = false;
      state.error = false;
      state.userData = action.payload;
    })
    .addCase(UserActionCreator.userSignIn.rejected, (state) => {
      state.pending = false;
      state.error = true;
    })

    // ─────────────────────────────── SIGN UP
    .addCase(UserActionCreator.userSignUp.pending, (state) => {
      state.pending = true;
      state.error = false;
      state.errorMessage = null;
      state.errorType = null;
      state.fieldErrors = null;
    })
    .addCase(UserActionCreator.userSignUp.fulfilled, (state, action) => {
      const result = handleUserResponse(action);
      state.pending = result.pending;
      state.error = result.error;
      state.errorType = result.errorType;
      state.errorField = result.errorField;
      state.fieldErrors = result.fieldErrors;
      state.errorMessage = result.generalMessage; // общее сообщение (не полевое)

      // сохраняем пользователя только при успешной регистрации
      if (!result.error) {
        state.userData = result.userData;
      }
    })

    // ─────────────────────────────── USER INFO
    .addCase(UserActionCreator.getUserInfo.pending, (state) => {
      state.pending = true;
      state.error = false;
      state.errorMessage = null;
    })
    .addCase(UserActionCreator.getUserInfo.fulfilled, (state, action) => {
      state.pending = false;
      state.error = false;

      const currentToken = state.userData?.data?.token ?? null;
      const profileData = action.payload?.data;
      const user = profileData?.user ?? profileData ?? null;

      state.userData = {
        data: {
          ...profileData,
          user,
          token: currentToken,
        },
      };
    })
    .addCase(UserActionCreator.getUserInfo.rejected, (state, action) => {
      state.pending = false;
      state.error = true;

      const status = (action.payload as { status?: number } | undefined)
        ?.status;
      if (status === 401) {
        state.userData.data.token = null;
        state.userData.data.user = null;
      }
    })

    // ─────────────────────────────── CONTACTS
    .addCase(UserActionCreator.getContacts.pending, (state) => {
      state.contacts.pending = true;
      state.contacts.error = false;
    })
    .addCase(UserActionCreator.getContacts.fulfilled, (state, action) => {
      state.contacts.pending = false;
      state.contacts.error = false;
      state.contacts.data = action.payload.data;
    })
    .addCase(UserActionCreator.getContacts.rejected, (state) => {
      state.contacts.pending = false;
      state.contacts.error = true;
    })

    // ─────────────────────────────── UPDATE PROFILE
    .addCase(UserActionCreator.updateProfile.pending, (state) => {
      state.updateProfile.pending = true;
      state.updateProfile.error = false;
      state.updateProfile.errorMessage = null;
      state.updateProfile.success = false;
      state.updateProfile.fieldErrors = {};
    })
    .addCase(UserActionCreator.updateProfile.fulfilled, (state, action) => {
      state.updateProfile.pending = false;
      state.updateProfile.error = false;
      state.updateProfile.success = true;
      state.updateProfile.errorMessage = null;
      state.updateProfile.fieldErrors = {};

      const payload = action.payload;
      const updatedUser =
        payload?.data?.user ??
        payload?.user ??
        (payload?.data && !payload?.data?.token ? payload.data : null);

      if (updatedUser) {
        const prevToken = state.userData?.data?.token ?? null;
        state.userData = {
          data: {
            user: updatedUser,
            token: prevToken,
          },
        };
      }
    })
    .addCase(UserActionCreator.updateProfile.rejected, (state, action) => {
      state.updateProfile.pending = false;
      state.updateProfile.error = true;
      state.updateProfile.success = false;

      const payload = action.payload || {};
      const fallbackMsg = action?.error?.message || null;

      state.updateProfile.errorMessage = payload.message ?? fallbackMsg ?? null;
      state.updateProfile.fieldErrors = payload.errors ?? {};
    })

    // ─────────────────────────────── LOGOUT
    .addCase(UserActionCreator.logout, (state) => {
      state.userData.data.token = null;
      state.userData.data.user = null;
    })

    // ─────────────────────────────── SESSION BOOTSTRAP
    .addCase(UserActionCreator.restoreSessionToken, (state, action) => {
      state.userData.data.token = action.payload;
    })

    // ─────────────────────────────── REMEMBER ME
    .addCase(UserActionCreator.setRememberMe, (state, action) => {
      state.rememberMe = action.payload;
    })

    // ─────────────────────────────── VERIFY EMAIL
    .addCase(UserActionCreator.verifyEmail.pending, (state) => {
      state.verifyEmail.pending = true;
      state.verifyEmail.error = false;
      state.verifyEmail.errorKind = null;
      state.verifyEmail.success = false;
    })
    .addCase(UserActionCreator.verifyEmail.fulfilled, (state) => {
      state.verifyEmail.pending = false;
      state.verifyEmail.error = false;
      state.verifyEmail.errorKind = null;
      state.verifyEmail.success = true;
    })
    .addCase(UserActionCreator.verifyEmail.rejected, (state, action) => {
      state.verifyEmail.pending = false;
      state.verifyEmail.error = true;
      state.verifyEmail.errorKind =
        (action.payload as { kind?: string } | undefined)?.kind ||
        "retryable";
      state.verifyEmail.success = false;
    })

    // ─────────────────────────────── NOTIFICATION
    .addCase(UserActionCreator.setNotification, (state, action) => {
      state.notification.isOpen = action.payload.isOpen;
      state.notification.value = action.payload.value;
    })

    // ─────────────────────────────── RESET PASSWORD
    .addCase(UserActionCreator.resetPassword.pending, (state) => {
      state.resetPassword.pending = true;
      state.resetPassword.error = false;
      state.resetPassword.errorMessage = null;
      state.resetPassword.success = false;
    })
    .addCase(UserActionCreator.resetPassword.fulfilled, (state, action) => {
      state.resetPassword = handleResetPasswordResponse(action);
    })
    .addCase(UserActionCreator.resetPassword.rejected, (state, action) => {
      state.resetPassword.pending = false;
      state.resetPassword.error = true;
      state.resetPassword.errorMessage =
        action?.error?.message ?? "Не удалось сбросить пароль";
      state.resetPassword.success = false;
    })

    // ─────────────────────────────── PAYMENT
    .addCase(UserActionCreator.payment.pending, (state) => {
      state.payment.pending = true;
      state.payment.error = false;
      state.payment.errorMessage = null;
      state.payment.success = false;
    })
    .addCase(UserActionCreator.payment.fulfilled, (state, action) => {
      const result = handleResponse(action);
      state.payment = result;
    })
    .addCase(UserActionCreator.payment.rejected, (state) => {
      state.payment.pending = false;
      state.payment.error = true;
    })

    // ─────────────────────────────── SUBSCRIPTION PLANS
    .addCase(UserActionCreator.getSubscriptionPlans.pending, (state) => {
      state.subscriptionPlans.pending = true;
      state.subscriptionPlans.error = false;
      state.subscriptionPlans.errorMessage = null;
    })
    .addCase(
      UserActionCreator.getSubscriptionPlans.fulfilled,
      (state, action) => {
        state.subscriptionPlans.pending = false;
        state.subscriptionPlans.error = false;
        state.subscriptionPlans.data = action.payload.data;
      })
      .addCase(UserActionCreator.getSubscriptionPlans.rejected, (state) => {
        state.subscriptionPlans.pending = false;
        state.subscriptionPlans.error = true;
      })
      // ─────────────────────────────── CREATE CLIENT IN BASE
      .addCase(UserActionCreator.createClientInBase.pending, (state) => {
        state.createClient.pending = true;
        state.createClient.error = false;
        state.createClient.errorMessage = null;
        state.createClient.success = false;
        state.createClient.fieldErrors = {};
        state.createClient.data = null;
      })
      .addCase(UserActionCreator.createClientInBase.fulfilled, (state, action) => {
        state.createClient.pending = false;
        state.createClient.error = false;
        state.createClient.success = true;
        state.createClient.errorMessage = null;
        state.createClient.fieldErrors = {};

        const payload = action.payload;

        // если бэк возвращает что-то вроде { data: { ...client } }
        state.createClient.data = payload?.data ?? payload ?? null;
        const createdClient = payload?.data ?? payload ?? null;

        if (createdClient?.id) {
          const list = Array.isArray(state.clientsBase.data) ? state.clientsBase.data : [];
          const exists = list.some((c: any) => String(c.id) === String(createdClient.id));
          if (!exists) {
            state.clientsBase.data = [createdClient, ...list];
          }
        }
      })
      .addCase(UserActionCreator.createClientInBase.rejected, (state, action) => {
        state.createClient.pending = false;
        state.createClient.error = true;
        state.createClient.success = false;

        const payload: any = action.payload || {};
        state.createClient.errorMessage =
            payload.message ?? "Не удалось добавить клиента. Попробуйте ещё раз.";
        state.createClient.fieldErrors = payload.errors ?? {};
      })
      // 🆕 ───────────────────────────── GET CLIENTS BASE
      .addCase(UserActionCreator.getClientsBase.pending, (state, action) => {
        const isLoadingMore = Boolean(action.meta.arg?.cursor);
        state.clientsBase.pending = !isLoadingMore;
        state.clientsBase.loadingMore = isLoadingMore;
        state.clientsBase.error = false;
        state.clientsBase.errorMessage = null;
        if (!isLoadingMore) {
          state.clientsBase.meta.nextCursor = null;
          state.clientsBase.meta.hasMore = false;
        }
      })
      .addCase(UserActionCreator.getClientsBase.fulfilled, (state, action) => {
        state.clientsBase.pending = false;
        state.clientsBase.loadingMore = false;
        state.clientsBase.error = false;

        const payload = action.payload;
        const incoming = Array.isArray(payload?.data) ? payload.data : [];
        const isLoadingMore = Boolean(action.meta.arg?.cursor);

        if (isLoadingMore) {
          const existing = Array.isArray(state.clientsBase.data)
              ? state.clientsBase.data
              : [];
          const existingIds = new Set(existing.map((client: any) => String(client.id)));
          state.clientsBase.data = [
            ...existing,
            ...incoming.filter(
                (client: any) => !existingIds.has(String(client.id)),
            ),
          ];
        } else {
          state.clientsBase.data = incoming;
        }

        state.clientsBase.meta = {
          perPage: payload?.meta?.perPage ?? action.meta.arg?.perPage ?? 20,
          nextCursor: payload?.meta?.nextCursor ?? null,
          hasMore: Boolean(payload?.meta?.hasMore),
          periodFrom:
              payload?.meta?.periodFrom ?? action.meta.arg?.periodFrom ?? null,
          periodTo:
              payload?.meta?.periodTo ?? action.meta.arg?.periodTo ?? null,
        };
      })
      .addCase(UserActionCreator.getClientsBase.rejected, (state, action) => {
        state.clientsBase.pending = false;
        state.clientsBase.loadingMore = false;
        state.clientsBase.error = true;

        const payload: any = action.payload || {};
        state.clientsBase.errorMessage =
            payload.message ?? "Не удалось загрузить клиентов. Попробуйте ещё раз.";
      })
      // ─────────────────────────────── CLIENT DEALS
      .addCase(UserActionCreator.getClientDeals.pending, (state, action) => {
        const id = action.meta?.arg?.clientId;
        state.clientDeals.pending = true;
        state.clientDeals.error = false;
        state.clientDeals.currentLoadingId = id ?? null; // 👈 записываем id клиента
      })
      .addCase(UserActionCreator.getClientDeals.fulfilled, (state, action) => {
        const { clientId, data } = action.payload || {};
        state.clientDeals.pending = false;
        state.clientDeals.error = false;
        state.clientDeals.data[clientId] = Array.isArray(data) ? data : [];
        state.clientDeals.currentLoadingId = null;
      })
      .addCase(UserActionCreator.getClientDeals.rejected, (state, action) => {
        state.clientDeals.pending = false;
        state.clientDeals.error = true;
        state.clientDeals.errorMessage =
            action.payload?.message ?? "Не удалось загрузить сделки клиента.";
        state.clientDeals.currentLoadingId = null; // 👈 очищаем и при ошибке
      })
      // ─────────────────────────────── CREATE CLIENT DEAL
      .addCase(UserActionCreator.createClientDeal.pending, (state) => {
        state.createDeal.pending = true;
        state.createDeal.error = false;
        state.createDeal.errorMessage = null;
      })
      .addCase(UserActionCreator.createClientDeal.fulfilled, (state, action) => {
        state.createDeal.pending = false;
        state.createDeal.error = false;
        state.createDeal.errorMessage = null;

        const { clientId, deal } = action.payload || {};
        if (clientId && deal) {
          const existing = state.clientDeals.data[clientId] || [];
          state.clientDeals.data[clientId] = [...existing, deal];
        }
      })
      .addCase(UserActionCreator.createClientDeal.rejected, (state, action) => {
        state.createDeal.pending = false;
        state.createDeal.error = true;

        const payload: any = action.payload || {};
        state.createDeal.errorMessage =
            payload.message || "Не удалось создать сделку.";
      })
      // ─────────────────────────────── UPDATE CLIENT DEAL
      .addCase(UserActionCreator.updateClientDeal.pending, (state) => {
        state.updateDeal.pending = true;
        state.updateDeal.error = false;
        state.updateDeal.errorMessage = null;
      })
      .addCase(UserActionCreator.updateClientDeal.fulfilled, (state, action) => {
        state.updateDeal.pending = false;
        state.updateDeal.error = false;
        state.updateDeal.errorMessage = null;

        const { clientId, deal } = action.payload || {};
        if (!clientId || !deal?.id) return;

        const list = state.clientDeals.data[clientId] || [];
        state.clientDeals.data[clientId] = list.map((d: any) =>
            String(d.id) === String(deal.id) ? deal : d
        );
      })
      .addCase(UserActionCreator.updateClientDeal.rejected, (state, action) => {
        state.updateDeal.pending = false;
        state.updateDeal.error = true;

        const payload: any = action.payload || {};
        state.updateDeal.errorMessage =
            payload.message || "Не удалось обновить сделку.";
      })
      .addCase(UserActionCreator.addClientDealComment.pending, (state) => {
        state.addDealComment.pending = true;
        state.addDealComment.error = false;
        state.addDealComment.errorMessage = null;
      })
      .addCase(UserActionCreator.addClientDealComment.fulfilled, (state) => {
        state.addDealComment.pending = false;
        state.addDealComment.error = false;
        state.addDealComment.errorMessage = null;
        // комментарий в стор можно не вшивать — мы рефетчим сделки
      })
      .addCase(UserActionCreator.addClientDealComment.rejected, (state, action) => {
        state.addDealComment.pending = false;
        state.addDealComment.error = true;
        state.addDealComment.errorMessage =
            action.payload?.message || "Не удалось добавить комментарий.";
      })
      // 🆕 ─────────────────────────────── ATTACH DEAL ATTACHMENT
      .addCase(UserActionCreator.attachClientDealAttachment.pending, (state) => {
        state.attachDealAttachment.pending = true;
        state.attachDealAttachment.error = false;
        state.attachDealAttachment.errorMessage = null;
      })
      .addCase(UserActionCreator.attachClientDealAttachment.fulfilled, (state) => {
        state.attachDealAttachment.pending = false;
        state.attachDealAttachment.error = false;
        state.attachDealAttachment.errorMessage = null;
        // если нужно — можно потом сюда добавить логику обновления deal.attachments
      })
      .addCase(
          UserActionCreator.attachClientDealAttachment.rejected,
          (state, action) => {
            state.attachDealAttachment.pending = false;
            state.attachDealAttachment.error = true;
            state.attachDealAttachment.errorMessage =
                action.payload?.message ||
                "Не удалось прикрепить расчёт к сделке.";
          },
      )
      // ─────────────────────────────── GET DEAL ATTACHMENTS
      .addCase(UserActionCreator.getDealAttachments.pending, (state, action) => {
        state.clientDeals.pending = true;

        // по-хорошему можно записать id сделки или клиента,
        // но чтобы не трогать существующую структуру:
        state.clientDeals.error = false;
        state.clientDeals.errorMessage = null;
      })
      .addCase(UserActionCreator.getDealAttachments.fulfilled, (state, action) => {
        state.clientDeals.pending = false;
        state.clientDeals.error = false;
        state.clientDeals.errorMessage = null;

        const { clientId, dealId, attachments } = action.payload || {};
        if (!clientId || !dealId) return;

        const list = state.clientDeals.data[clientId] || [];

        // обновляем только одну сделку
        state.clientDeals.data[clientId] = list.map((d: any) =>
            String(d.id) === String(dealId)
                ? {
                  ...d,
                  attachments:
                      attachments && typeof attachments === "object"
                          ? attachments
                          : { sharedCalc: [], collection: [] },
                }
                : d,
        );
      })
      .addCase(UserActionCreator.getDealAttachments.rejected, (state, action) => {
        state.clientDeals.pending = false;
        state.clientDeals.error = true;
        state.clientDeals.errorMessage =
            action.payload?.message ||
            "Не удалось получить вложения сделки.";
      })
      .addCase(UserActionCreator.getDealStatuses.pending, (state) => {
        state.dealStatuses.pending = true;
        state.dealStatuses.error = false;
        state.dealStatuses.errorMessage = null;
      })
      .addCase(UserActionCreator.getDealStatuses.fulfilled, (state, action) => {
        state.dealStatuses.pending = false;
        state.dealStatuses.error = false;
        state.dealStatuses.errorMessage = null;

        const { statuses, total } = action.payload || {};

        state.dealStatuses.data = Array.isArray(statuses) ? statuses : [];
        state.dealStatuses.total =
            typeof total === "number" ? total : null;
      })
      .addCase(UserActionCreator.getDealStatuses.rejected, (state, action) => {
        state.dealStatuses.pending = false;
        state.dealStatuses.error = true;

        const payload: any = action.payload || {};
        state.dealStatuses.errorMessage =
            payload.message ||
            "Не удалось загрузить статусы сделок.";
      });
});
